import { BehaviorLabel, GetMoedaDescription } from 'utils/strings';

import {
  BlockedReason,
  COIN_KEYS,
  COIN_TIERS,
  CoinKey,
  LIMIT_PER_TARGET,
  ceilingFor,
  coinUnavailableReason,
  hasAnyRecognitionAvailable,
  optionState,
  pointOptionsFor,
} from '../recognition-points';
import { BehaviorKey } from '../recognition';
import { ChoiceTile, ILLUSTRATED_TILE_CLASSES } from './choice-tile';
import { COIN_ART } from './recognition-art';
import { Emphasis, StepHeading, StepPanel } from './step-layout';

const COIN_BLOCKED_LABEL: Record<BlockedReason, string> = {
  'no-balance': 'Saldo insuficiente',
  'over-limit': 'Limite atingido',
};

const POINTS_BLOCKED_REASON: Record<BlockedReason, string> = {
  'no-balance': 'saldo insuficiente',
  'over-limit': `ultrapassa o limite de ${LIMIT_PER_TARGET} pontos para esta pessoa nesta fase`,
};

interface CoinPointsStepProps {
  colleagueName: string;
  behavior?: BehaviorKey;
  coin?: CoinKey;
  points?: number;
  /** Saldo `point_behavior` de quem envia. */
  balance: number;
  /** Pontos já enviados a este colega na fase. */
  pointsSent: number;
  onSelectCoin: (coin: CoinKey) => void;
  onSelectPoints: (points: number) => void;
}

/**
 * Rótulo da pílula do tipo. O cartão não anuncia um valor fechado: anuncia o teto da faixa,
 * e o valor é escolhido logo abaixo. Bloqueado, a pílula diz o motivo.
 */
function coinCapLabel(coin: CoinKey, balance: number, pointsSent: number) {
  const blocked = coinUnavailableReason(coin, balance, pointsSent);

  return blocked ? COIN_BLOCKED_LABEL[blocked] : `Até ${ceilingFor(coin)} pontos`;
}

function CoinOptions({
  coin,
  balance,
  pointsSent,
  onSelectCoin,
}: Pick<CoinPointsStepProps, 'coin' | 'balance' | 'pointsSent' | 'onSelectCoin'>) {
  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className="screen-reader-text">Tipo de reconhecimento</legend>

      {/* No celular cada tipo é uma linha (ilustração, nome e a pílula à direita): em três colunas
          de 94px o nome e a pílula não cabiam sem quebrar. Da largura `sm` em diante, três cartões. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
        {COIN_KEYS.map(key => {
          const selected = coin === key;

          return (
            <ChoiceTile
              key={key}
              name="tipo"
              value={key}
              checked={selected}
              disabled={coinUnavailableReason(key, balance, pointsSent) !== null}
              onSelect={() => onSelectCoin(key)}
              className={`${ILLUSTRATED_TILE_CLASSES} gap-3 px-3 py-2 sm:min-h-[122px] sm:flex-col sm:justify-center sm:gap-2.5 sm:px-2.5 sm:py-4`}
            >
              <img src={COIN_ART[key]} alt="" className="h-10 w-10 shrink-0 object-contain sm:h-14 sm:w-14" />
              <span
                className={`font-display text-[13px] sm:text-xs ${selected ? 'font-extrabold text-brand-blue-dark' : 'font-bold text-content'}`}
              >
                {COIN_TIERS[key].label}
              </span>
              {/* O separador é só para o leitor de tela, que sem ele leria "#VALEUAté 100 pontos". */}
              <span className="screen-reader-text">: </span>
              <span
                className={`ml-auto whitespace-nowrap rounded-jj-pill px-[11px] py-1 text-center text-[11px] font-semibold sm:ml-0 sm:text-[10.5px] ${
                  selected ? 'bg-brand-blue-dark text-brand-white' : 'bg-brand-purple-light text-brand-blue-dark'
                }`}
              >
                {coinCapLabel(key, balance, pointsSent)}
              </span>
            </ChoiceTile>
          );
        })}
      </div>
    </fieldset>
  );
}

function PointsOptions({
  coin,
  points,
  balance,
  pointsSent,
  onSelectPoints,
}: { coin: CoinKey } & Pick<CoinPointsStepProps, 'points' | 'balance' | 'pointsSent' | 'onSelectPoints'>) {
  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className="mb-3 p-0 font-display text-sm font-bold text-content">Agora pontue esse reconhecimento:</legend>

      <div className="grid grid-cols-5 gap-1.5 sm:gap-2.5">
        {pointOptionsFor(coin).map(option => {
          const state = optionState(coin, option, balance, pointsSent);
          const selected = points === option;

          return (
            <ChoiceTile
              key={option}
              name="pontos"
              value={String(option)}
              checked={selected}
              disabled={state !== 'enabled'}
              onSelect={() => onSelectPoints(option)}
              ariaLabel={
                state === 'enabled' ? `${option} pontos` : `${option} pontos. Indisponível: ${POINTS_BLOCKED_REASON[state]}`
              }
              className={`items-center justify-center rounded-jj-pill border-2 px-1 py-2.5 font-display text-[17px] font-bold sm:py-[15px] sm:text-[21px] ${
                selected ? 'text-brand-blue-dark' : 'text-content'
              }`}
            >
              {option}
            </ChoiceTile>
          );
        })}
      </div>
    </fieldset>
  );
}

/** Etapa 3: o tipo do reconhecimento e, dentro da faixa dele, o valor (REC-26 a REC-31). */
export function CoinPointsStep({
  colleagueName,
  behavior,
  coin,
  points,
  balance,
  pointsSent,
  onSelectCoin,
  onSelectPoints,
}: CoinPointsStepProps) {
  if (!hasAnyRecognitionAvailable(balance, pointsSent)) {
    return (
      <div>
        <StepHeading hidden>Tipo e pontos</StepHeading>
        <p className="m-0 rounded-jj-lg bg-brand-yellow-light px-4 py-3 text-center text-sm text-content">
          Você não possui saldo de pontos para reconhecer. Aguarde até a próxima distribuição.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      <StepHeading>
        Escolha o tipo de Reconhecimento que você deseja enviar para <Emphasis>{colleagueName}</Emphasis> no comportamento{' '}
        <Emphasis>
          <span className="uppercase">{behavior ? BehaviorLabel[behavior] : ''}</span>
        </Emphasis>
      </StepHeading>

      <StepPanel className="gap-4">
        <CoinOptions coin={coin} balance={balance} pointsSent={pointsSent} onSelectCoin={onSelectCoin} />

        {coin && (
          <>
            <PointsOptions
              coin={coin}
              points={points}
              balance={balance}
              pointsSent={pointsSent}
              onSelectPoints={onSelectPoints}
            />
            <p aria-live="polite" className="m-0 text-[12.5px] leading-normal text-content [text-wrap:pretty]">
              {GetMoedaDescription(coin)}
            </p>
          </>
        )}
      </StepPanel>
    </div>
  );
}
